import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router';

import 'katex/dist/katex.min.css';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

interface MarkdownRendererProps {
  content: string;
}

const PRAXIS_DOMAIN_RE = /^https?:\/\/[^/]*praxis[^/]*/i;

const stripPraxisDomain = (href: string | undefined): string | undefined => {
  if (!href) {
    return href;
  }
  return href.replace(PRAXIS_DOMAIN_RE, '');
};

const findCommandBlocks = (
  formula: string,
  commandPattern: RegExp
): Array<{ start: number; end: number }> => {
  const blocks: Array<{ start: number; end: number }> = [];

  for (const match of formula.matchAll(commandPattern)) {
    const start = match.index;
    let depth = 1;
    let pos = match.index + match[0].length;

    while (pos < formula.length && depth > 0) {
      if (formula[pos] === '{') {
        depth++;
      } else if (formula[pos] === '}') {
        depth--;
      }
      pos++;
    }

    if (depth === 0) {
      blocks.push({ start, end: pos - 1 });
    }
  }

  return blocks;
};

const wrapTextInFormula = (formula: string): string => {
  const textBlocks = findCommandBlocks(formula, /\\text\{/g);
  const mathrmBlocks = findCommandBlocks(formula, /\\mathrm\{/g);
  const allBlocks = [...textBlocks, ...mathrmBlocks];

  const isInsideTextBlock = (position: number): boolean => {
    return allBlocks.some(
      (block) => position >= block.start && position <= block.end
    );
  };

  return formula.replace(
    /\b([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+)*)\b/g,
    (match, text, offset) => {
      const charBefore = offset > 0 ? formula[offset - 1] : '';
      const isInCommand = charBefore === '\\';

      if (isInCommand) {
        return match;
      }

      const matchEnd = offset + match.length - 1;
      if (isInsideTextBlock(offset) || isInsideTextBlock(matchEnd)) {
        return match;
      }

      return `\\text{${text}}`;
    }
  );
};

const normalizeLatexDelimiters = (text: string): string => {
  let normalized = text.replace(/\\\[([\s\S]*?)\\\]/g, '$$$1$$');

  normalized = normalized.replace(/\[([\s\S]*?)\]/g, (match, content) => {
    if (/\\[a-zA-Z]/.test(content)) {
      return `$$${content}$$`;
    }
    return match;
  });

  normalized = normalized.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
    const wrappedFormula = wrapTextInFormula(formula);
    return `\n\n$$${wrappedFormula}$$\n\n`;
  });

  return normalized;
};

// eslint-disable-next-line max-lines-per-function
export const MarkdownRenderer = ({ content }: MarkdownRendererProps) => {
  const normalizedContent = normalizeLatexDelimiters(content);

  return (
    <div className="markdown-content">
      <style>{`
        .markdown-content .katex {
          font-family: inherit;
        }
        .markdown-content .katex .mtext {
          font-family: inherit;
          font-style: normal;
          font-weight: inherit;
        }
        .markdown-content .katex .mord.text {
          font-family: inherit;
          font-style: normal;
        }
        .markdown-content .katex .text {
          font-family: inherit;
          font-style: normal;
        }
      `}</style>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          h1: ({ children }) => {
            const text =
              typeof children === 'string'
                ? children
                : children?.toString() || '';
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)/g, '');
            return (
              <h1
                id={id}
                className="text-4xl font-bold mb-6 mt-8 first:mt-0 text-foreground"
              >
                {children}
              </h1>
            );
          },
          h2: ({ children }) => {
            const text =
              typeof children === 'string'
                ? children
                : children?.toString() || '';
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)/g, '');
            return (
              <h2
                id={id}
                className="text-2xl font-semibold mb-4 mt-8 first:mt-0 text-foreground"
              >
                {children}
              </h2>
            );
          },
          h3: ({ children }) => {
            const text =
              typeof children === 'string'
                ? children
                : children?.toString() || '';
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)/g, '');
            return (
              <h3
                id={id}
                className="text-xl font-semibold mb-3 mt-6 first:mt-0 text-foreground"
              >
                {children}
              </h3>
            );
          },
          p: ({ children }) => (
            <p className="mb-4 text-foreground leading-relaxed last:mb-0">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-6 mb-4 space-y-2 text-foreground">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-6 mb-4 space-y-2 text-foreground">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="text-foreground leading-relaxed">{children}</li>
          ),
          strong: ({ children }) => (
            <strong className="font-bold text-foreground">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic text-foreground">{children}</em>
          ),
          code: ({ children }) => (
            <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground border">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto mb-4 border">
              {children}
            </pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/30 pl-4 mb-4 text-muted-foreground italic bg-muted/30 py-2 rounded-r">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="border-border my-6" />,
          table: ({ children }) => (
            <div className="mb-4 overflow-x-auto border border-border rounded-lg">
              <table className="w-full border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-muted/50 border-b border-border">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="divide-y divide-border">{children}</tbody>
          ),
          tr: ({ children }) => (
            <tr className="border-b border-border last:border-0">{children}</tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-3 text-left font-semibold text-foreground border-r border-border last:border-r-0">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-3 text-foreground border-r border-border last:border-r-0">
              {children}
            </td>
          ),
          a: ({ href, children }) => {
            const resolvedHref = stripPraxisDomain(href);

            if (resolvedHref?.startsWith('#')) {
              return (
                <a
                  href={resolvedHref}
                  className="text-primary underline hover:opacity-80 transition-opacity"
                >
                  {children}
                </a>
              );
            }
            if (resolvedHref?.startsWith('/')) {
              return (
                <Link
                  to={resolvedHref}
                  className="text-primary underline hover:opacity-80 transition-opacity"
                >
                  {children}
                </Link>
              );
            }
            return (
              <a
                href={resolvedHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:opacity-80 transition-opacity"
              >
                {children}
              </a>
            );
          },
        }}
      >
        {normalizedContent}
      </ReactMarkdown>
    </div>
  );
};
